import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';

import { SelectTriggerOption } from '../components/TriggerTypeSelectorModal';
import { EventTriggerType } from '../domain';
import { usePendingTriggersStore } from '../stores/pendingTriggersStore';
import { UserIdentifierProperty } from '../types';

export interface MutationMeta {
  triggerType: EventTriggerType;
  triggerOption: SelectTriggerOption;
  userIdentifierProperty: UserIdentifierProperty;
}

interface UseCreateTriggerMutationOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  editingTriggerId?: string | null;
}

interface CreateTriggerMutationResult {
  mutate: (formValues: Record<string, unknown>, meta: MutationMeta) => void;
  isPending: boolean;
}

export const useCreateTriggerMutation = (options?: UseCreateTriggerMutationOptions): CreateTriggerMutationResult => {
  const addTrigger = usePendingTriggersStore((s) => s.addTrigger);
  const updateTrigger = usePendingTriggersStore((s) => s.updateTrigger);

  const mutate = useCallback(
    (formValues: Record<string, unknown>, meta: MutationMeta) => {
      try {
        if (options?.editingTriggerId) {
          updateTrigger(options.editingTriggerId, {
            triggerType: meta.triggerType,
            triggerOption: meta.triggerOption,
            formValues,
            userIdentifierProperty: meta.userIdentifierProperty,
          });
          toast.success('Trigger updated');
        } else {
          addTrigger({
            triggerType: meta.triggerType,
            triggerOption: meta.triggerOption,
            formValues,
            userIdentifierProperty: meta.userIdentifierProperty,
          });
          toast.success('Trigger added to list');
        }
        options?.onSuccess?.();
      } catch (err) {
        toast.error(options?.editingTriggerId ? 'Failed to update trigger' : 'Failed to add trigger');
        options?.onError?.(err as Error);
      }
    },
    [addTrigger, updateTrigger, options],
  );

  return useMemo(
    () => ({
      mutate,
      isPending: false,
    }),
    [mutate],
  );
};
